import React, { useState } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { FaMoneyBillWave, FaBuilding, FaGraduationCap, FaBook, FaCalendar, FaIdCard, FaUsers, FaUniversity, FaFileInvoice } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import axios from '../api/axios';
import GenericCrudPage from '../components/GenericCrudPage';
import {
  useColleges,
  useDegrees,
  usePrograms,
  useAcademicYears,
  useProgramYears,
  useSemesters,
  useCategories,
  useFeeType,
} from '../hooks/useDropdownData';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const FeeMaster = () => {
  const [filterDegree, setFilterDegree] = useState("");
  const [fetchConfig, setFetchConfig] = useState({ degree: 0 });

  const { colleges, loading: collegesLoading } = useColleges();
  const { degrees, loading: degreesLoading } = useDegrees();
  const { programmes: programs, loading: programsLoading } = usePrograms();
  const { academicYears, loading: academicYearsLoading } = useAcademicYears();
  const { programmeYears: programYears, loading: programYearsLoading } = useProgramYears();
  const { semesters, loading: semestersLoading } = useSemesters();
  const { categories, loading: categoriesLoading } = useCategories();
  const { feeTypes, loading: feeTypesLoading } = useFeeType();

  const dropdownsLoading = collegesLoading || degreesLoading || programsLoading ||
                           academicYearsLoading || programYearsLoading || semestersLoading ||
                           categoriesLoading || feeTypesLoading;

  const customFetch = async () => {
    const token = Cookies.get('token');
    
    if (fetchConfig.degree && fetchConfig.degree !== 0) {
      try {
        const res = await axios.get(`${BASE_URL}/master/getFeeMaster?degree=${fetchConfig.degree}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        return res.data?.feemaster || [];
      } catch (error) {
        console.error("Failed to fetch fee master by degree:", error);
        toast.error("Could not fetch fee data for the selected degree.");
        return [];
      }
    } 
    else {
      try {
        const res = await axios.get(`${BASE_URL}/master/getFeeMaster`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        return res.data?.feemaster || [];
      } catch (error) {
        console.error("Failed to fetch all fees:", error);
        toast.error("Could not fetch fee data.");
        return [];
      }
    }
  };

  const fetchFeeById = async (item) => {
    if (!item || !item.id) {
      toast.error("Invalid fee record selected.");
      return null;
    }
    try {
      const token = Cookies.get('token');
      const res = await axios.get(`${BASE_URL}/master/getFeeMasterOnId?id=${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("Fee Master by ID response:", res.data);
      
      const feeData = res.data?.feemaster;
      if (feeData && (Array.isArray(feeData) ? feeData.length > 0 : feeData)) {
        const detailedFee = Array.isArray(feeData) ? feeData[0] : feeData;
        console.log("Detailed Fee Data:", detailedFee);
        return detailedFee;
      } else {
        toast.warn(`Could not find details for fee ID: ${item.id}.`);
        return null;
      }
    } catch (error) {
      console.error(`Failed to fetch fee by ID ${item.id}:`, error);
      toast.error("An error occurred while fetching fee details.");
      return null;
    }
  };

  const formFields = [
    {
      title: 'Fee Information',
      fields: [
        { 
          name: 'exFeeName', 
          label: 'Fee Name', 
          icon: FaFileInvoice, 
          required: true,
          placeholder: 'Enter fee name (e.g., Tuition Fee, Lab Fee)'
        },
        { 
          name: 'feeAmount', 
          label: 'Fee Amount', 
          icon: FaMoneyBillWave, 
          required: true, 
          type: 'number', 
          min: 0, 
          step: '0.01',
          placeholder: 'Enter amount in rupees'
        },
        { 
          name: 'exAbAcDac', 
          label: 'Reference Code', 
          icon: FaFileInvoice, 
          placeholder: 'e.g., 122/189 or 21/1026'
        },
      ]
    },
    {
      title: 'Academic Configuration',
      fields: [
        { 
          name: 'colleges', 
          label: 'College', 
          icon: FaBuilding, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(colleges) ? colleges : [])
            .filter(Boolean)
            .filter(c => c && c.id && c.exName)
            .map(c => ({ value: c.id, label: c.exName || '' })) 
        },
        { 
          name: 'degree', 
          label: 'Degree', 
          icon: FaGraduationCap, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(degrees) ? degrees : [])
            .filter(Boolean)
            .filter(d => d && d.id && d.exName)
            .map(d => ({ value: d.id, label: d.exName || '' })) 
        },
        { 
          name: 'programmes', 
          label: 'Programs', 
          icon: FaBook, 
          required: true, 
          as: 'multiselect', 
          options: (Array.isArray(programs) ? programs : [])
            .filter(Boolean)
            .filter(p => p && p.id && p.exName)
            .map(p => ({ value: p.id, label: p.exName || '' })),
          helpText: 'Select one or more programs'
        },
        { 
          name: 'academicYear', 
          label: 'Academic Year', 
          icon: FaCalendar, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(academicYears) ? academicYears : [])
            .filter(Boolean)
            .filter(a => a && a.id && (a.exYear || a.exName))
            .map(a => ({ value: a.id, label: a.exYear || a.exName || '' })) 
        },
        { 
          name: 'programmeYear', 
          label: 'Programme Year', 
          icon: FaCalendar, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(programYears) ? programYears : [])
            .filter(Boolean)
            .filter(p => p && p.id && p.exName)
            .map(p => ({ value: p.id, label: p.exName || '' })) 
        },
        { 
          name: 'semester', 
          label: 'Semester', 
          icon: FaCalendar, 
          required: true, 
          as: 'select', 
          options: (Array.isArray(semesters) ? semesters : [])
            .filter(Boolean)
            .filter(s => s && s.id && s.exName)
            .map(s => ({ value: s.id, label: s.exName || '' })) 
        },
        { 
          name: 'category', 
          label: 'Categories', 
          icon: FaIdCard, 
          required: true, 
          as: 'multiselect', 
          options: (Array.isArray(categories) ? categories : [])
            .filter(Boolean)
            .filter(c => c && c.id && c.exName)
            .map(c => ({ value: c.id, label: c.exName || '' })),
          helpText: 'Select one or more categories'
        },
        { 
          name: 'feeType', 
          label: 'Fee Types', 
          icon: FaMoneyBillWave, 
          required: true, 
          as: 'multiselect', 
          options: (Array.isArray(feeTypes) && feeTypes.length > 0 ? feeTypes : [])
            .filter(Boolean)
            .filter(f => f && f.id && f.exFeeName)
            .map(f => ({ value: f.id, label: f.exFeeName || `Fee Type ${f.id}` })),
          helpText: feeTypes.length === 0 ? 'No fee types available' : 'Select one or more fee types'
        },
      ]
    },
    {
      title: 'Status',
      fields: [
        { 
          name: 'exIsActive', 
          label: 'Active Status', 
          icon: FaBuilding, 
          as: 'switch',
          helpText: 'Enable or disable this fee'
        },
      ]
    }
  ];

  const validateForm = (form) => {
    const errors = {};
    
    if (!form.exFeeName?.trim()) {
      errors.exFeeName = 'Fee name is required';
    }
    
    if (!form.feeAmount) {
      errors.feeAmount = 'Fee amount is required';
    } else if (isNaN(form.feeAmount) || Number(form.feeAmount) < 0) {
      errors.feeAmount = 'Fee amount must be a positive number';
    }
    
    if (!form.colleges) {
      errors.colleges = 'College is required';
    }
    
    if (!form.degree) {
      errors.degree = 'Degree is required';
    }
    
    if (!form.programmes || (Array.isArray(form.programmes) && form.programmes.length === 0)) {
      errors.programmes = 'At least one program is required';
    }
    
    if (!form.academicYear) {
      errors.academicYear = 'Academic year is required';
    }
    
    if (!form.programmeYear) {
      errors.programmeYear = 'Programme year is required';
    }
    
    if (!form.semester) {
      errors.semester = 'Semester is required';
    }
    
    if (!form.category || (Array.isArray(form.category) && form.category.length === 0)) {
      errors.category = 'At least one category is required';
    }
    
    if (!form.feeType || (Array.isArray(form.feeType) && form.feeType.length === 0)) {
      errors.feeType = 'At least one fee type is required';
    }
    
    return errors;
  };

  const transformPayload = (form, editId, userId) => {
    const payload = {
      exFeeName: form.exFeeName?.trim() || '',
      feeAmount: form.feeAmount ? String(form.feeAmount) : '0',
      colleges: Number(form.colleges) || 0,
      degree: Number(form.degree) || 0,
      programmes: Array.isArray(form.programmes) 
        ? form.programmes.map(p => Number(p)) 
        : [Number(form.programmes)].filter(Boolean),
      academicYear: Number(form.academicYear) || 0,
      programmeYear: Number(form.programmeYear) || 0,
      semester: Number(form.semester) || 0,
      category: Array.isArray(form.category) 
        ? form.category.map(c => Number(c)) 
        : [Number(form.category)].filter(Boolean),
      feeType: Array.isArray(form.feeType) 
        ? form.feeType.map(f => Number(f)) 
        : [Number(form.feeType)].filter(Boolean),
      exAbAcDac: form.exAbAcDac?.trim() || '',
      exIsActive: form.exIsActive ? 1 : 0,
      user: userId || 1,
    };

    if (editId) {
      payload.id = editId;
    }

    console.log('Fee Master Payload:', payload);
    return payload;
  };

  const transformToForm = (item) => {
    if (!item) return config.initialFormState;
    
    const extractIds = (obj, key) => {
      if (!obj || !obj[key]) return [];
      const value = obj[key];
      if (Array.isArray(value)) return value;
      return [value];
    };
    
    return {
      exFeeName: item.exFeeName || '',
      feeAmount: item.exFeeAmount || '',
      exAbAcDac: item.exAbAcDac || '',
      colleges: item.exCollegeId?.toString() || '',
      degree: item.exDegreeId?.toString() || '',
      programmes: extractIds(item.programeId, 'exId'),
      academicYear: item.exAcademicYearId?.toString() || '',
      programmeYear: item.exProgrammeYearId?.toString() || '',
      semester: item.semesterId?.toString() || '',
      category: extractIds(item.categoryId, 'exCategoryId'),
      feeType: extractIds(item.feeTypeId, 'exFeeTypeIds'),
      exIsActive: item.exIsActive === 1 || item.exIsActive === true,
    };
  };

  const config = {
    baseUrl: BASE_URL,
    endpoints: {
      fetch: '/master/getFeeMaster',
      save: '/master/saveFeeMaster',
      update: '/master/updateFeeMaster',
      delete: '/master/deleteFeeMaster'
    },
    validateForm: validateForm,
    initialFormState: {
      exFeeName: '',
      feeAmount: '',
      exAbAcDac: '',
      colleges: '',
      degree: '',
      programmes: [],
      academicYear: '',
      programmeYear: '',
      semester: '',
      category: [],
      feeType: [],
      exIsActive: 1,
      user: 1,
    },
    entityName: 'Fee',
    fetchMethod: 'CUSTOM',
    deleteMethod: 'GET',
    customFetch: customFetch,
  };

  const tableColumns = [
    { key: 'exFeeName', label: 'Fee Name', sortable: true },
    { key: 'exFeeAmount', label: 'Amount', sortable: true, render: (item) => `₹${item.exFeeAmount || 0}` },
    { key: 'exCollege', label: 'College', sortable: true, render: (item) => item.exCollege || '-' },
    { key: 'exDegree', label: 'Degree', sortable: true, render: (item) => item.exDegree || '-' },
    { key: 'exAcademicYear', label: 'Academic Year', sortable: true, render: (item) => item.exAcademicYear || '-' },
    { key: 'exProgrammeYear', label: 'Programme Year', sortable: true, render: (item) => item.exProgrammeYear || '-' },
    { key: 'semester', label: 'Semester', sortable: true, render: (item) => item.semester || '-' },
    { key: 'exAbAcDac', label: 'Reference', sortable: true, render: (item) => item.exAbAcDac || '-' },
  ];

  const csvHeaders = [
    { key: 'exFeeName', label: 'Fee Name' },
    { key: 'exFeeAmount', label: 'Amount' },
    { key: 'exCollege', label: 'College' },
    { key: 'exDegree', label: 'Degree' },
    { key: 'exAcademicYear', label: 'Academic Year' },
    { key: 'exProgrammeYear', label: 'Programme Year' },
    { key: 'semester', label: 'Semester' },
    { key: 'exAbAcDac', label: 'Reference Code' },
    { key: 'exIsActive', label: 'Status', render: (item) => item.exIsActive ? 'Active' : 'Inactive' }
  ];

  const getViewFields = (item) => {
    if (!item) return [];
    
    console.log("View Modal - Item received:", item);
    
    const extractArrayValue = (obj, key) => {
      if (!obj || !obj[key]) return '-';
      const value = obj[key];
      if (Array.isArray(value)) {
        return value.join(', ') || '-';
      }
      return value || '-';
    };
    
    const programNames = item.programeName?.exName 
      ? (Array.isArray(item.programeName.exName) 
          ? item.programeName.exName.join(', ') 
          : item.programeName.exName)
      : '-';
    
    const categoryNames = item.categoryName?.exCategoryName 
      ? (Array.isArray(item.categoryName.exCategoryName) 
          ? item.categoryName.exCategoryName.join(', ') 
          : item.categoryName.exCategoryName)
      : '-';
    
    const feeTypeNames = item.feeTypeName?.exFeeName 
      ? (Array.isArray(item.feeTypeName.exFeeName) 
          ? item.feeTypeName.exFeeName.join(', ') 
          : item.feeTypeName.exFeeName)
      : '-';
    
    console.log("Extracted Values:", {
      programs: programNames,
      categories: categoryNames,
      feeTypes: feeTypeNames
    });
    
    return [
      { label: 'Fee Name', value: item.exFeeName || '-', icon: FaFileInvoice },
      { label: 'Fee Amount', value: `₹${item.exFeeAmount || 0}`, icon: FaMoneyBillWave },
      { label: 'Reference Code', value: item.exAbAcDac || '-', icon: FaFileInvoice },
      { label: 'College', value: item.exCollege || '-', icon: FaBuilding },
      { label: 'Degree', value: item.exDegree || '-', icon: FaGraduationCap },
      { label: 'Programs', value: programNames, icon: FaBook },
      { label: 'Academic Year', value: item.exAcademicYear || '-', icon: FaCalendar },
      { label: 'Programme Year', value: item.exProgrammeYear || '-', icon: FaCalendar },
      { label: 'Semester', value: item.semester || '-', icon: FaCalendar },
      { label: 'Categories', value: categoryNames, icon: FaIdCard },
      { label: 'Fee Types', value: feeTypeNames, icon: FaMoneyBillWave },
      { label: 'Status', value: item.exIsActive ? 'Active' : 'Inactive', icon: FaBuilding },
    ];
  };

  const handleClearFilters = () => {
    setFilterDegree('');
    setFetchConfig({ degree: 0 });
  };

  return (
    <Container fluid className="py-4">
      {dropdownsLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading dropdown data...</span>
          </div>
          <p className="mt-3 text-muted">Loading form data...</p>
        </div>
      ) : (
        <>
          <Row className="mb-4 align-items-end">
            <Col md={6}>
              <Form.Label>Filter by Degree</Form.Label>
              <Form.Select
                value={filterDegree}
                onChange={(e) => {
                  const newDegree = e.target.value;
                  setFilterDegree(newDegree);
                  setFetchConfig({ degree: Number(newDegree) || 0 });
                }}
              >
                <option value="">All Degrees</option>
                {(Array.isArray(degrees) ? degrees : [])
                  .filter(Boolean)
                  .map((d) => (
                    <option key={d.id} value={d.id}>{d.exName}</option>
                  ))}
              </Form.Select>
            </Col>
            <Col md={3} className="d-flex justify-content-start">
              <Button variant="outline-secondary" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </Col>
          </Row>

          <GenericCrudPage
            key={JSON.stringify(fetchConfig)}
            title="Fee"
            pluralTitle="Fees"
            config={config}
            formFields={formFields}
            tableColumns={tableColumns}
            emptyIcon={FaMoneyBillWave}
            csvHeaders={csvHeaders}
            csvFilename="fee-master.csv"
            transformPayload={transformPayload}
            transformToForm={transformToForm}
            getViewFields={getViewFields}
            fetchItemForEdit={fetchFeeById}
            fetchItemForView={fetchFeeById}
          />
        </>
      )}
    </Container>
  );
};

export default FeeMaster;